import { useState } from 'react';
import i18n from '../../i18n/i18n';
import { Controller, useForm, SubmitHandler } from 'react-hook-form';

// Components
import SignEvalButton from '../SignEvalButton';
import SignEvalTextField from '../SignEvalTextField';
import TitleContentInfo from '../TitleContentInfo';
import SlotPicker from '../Slots/SlotPicker';

// MUI imports
import { SwipeableDrawer, Box, Typography, CircularProgress, Autocomplete, TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ArticleIcon from '@mui/icons-material/Article';

// Helpers
import { useTheme } from '../../hooks/useTheme';
import { useSessionStore } from '../../store/SessionStore';
import { getTraining } from '../../helpers/api/callTraining';
import { formatTraining } from '../../helpers/functions/formatTraining';
import { updateSession } from '../../helpers/api/callSession';
import { isNameValid } from '../../helpers/functions/validation';

// Types
import { SessionData } from '../../types/sessionData';
import { TrainingData } from '../../types/trainingData';
import { SlotData, SlotDayData } from '../../types/slotData';
import { SocietyData } from '../../types/societyData';
import { getSocieties } from '../../helpers/api/callSociety';

interface DrawerEditSessionProps {
    session: SessionData;
    slots: SlotDayData[];
    onSlotChange: (selectedSlot: SlotData) => void;
}

type FormValues = {
    sessionName: string;
    training: string;
    typeTraining?: string;
    societyId?: string;
};

const DrawerEditSession = ({ session, slots, onSlotChange }: DrawerEditSessionProps) => {
    const [openDrawer, setOpenDrawer] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [trainings, setTrainings] = useState<TrainingData[]>([]);
    const [societies, setSocieties] = useState<SocietyData[] | null>(null);

    const theme = useTheme();
    const { setSession } = useSessionStore();

    const typeTrainings = i18n.language === 'fr-FR' ? ['Intra', 'Inter'] : ['Internal', 'External'];

    const fetchTrainings = async () => {
        const trainings = await getTraining();
        const formattedTrainings = formatTraining(trainings);
        setTrainings(formattedTrainings);
    };

    const fetchSocieties = async () => {
        const societies = await getSocieties();
        setSocieties(societies);
    };

    const {
        register,
        handleSubmit,
        control,
        formState: { errors },
        clearErrors,
        setValue
    } = useForm<FormValues>();

    const handleClickOpen = async () => {
        await fetchTrainings();
        await fetchSocieties();
        setOpenDrawer(true);
    };

    const handleClose = () => {
        setOpenDrawer(false);
    };

    const onSubmit: SubmitHandler<FormValues> = async (data) => {
        setIsLoading(true);

        try {
            const training = trainings.find((training) => training.label === data.training);
            if (!training || !training.id) {
                console.error('Training not found or has no ID');
                return;
            }
            const response = await updateSession(session.id, data.sessionName, training.id.toString(), data.typeTraining, data.societyId);
            if (response.status === 204) {
                setSession({ ...session, sessionName: data.sessionName, trainingId: training.id.toString(), typeTraining: data.typeTraining, societyId: data.societyId });
                handleClose();
            } else {
                console.error('Error updating session:', response);
            }
        } catch (error) {
            console.error('Error fetching training:', error);
        }
    };

    return (
        <>
            <SignEvalButton size="large" backgroundColor="blue" onClick={handleClickOpen}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '0.5em'
                    }}
                >
                    <EditOutlinedIcon />
                    <Typography sx={{ color: theme.palette.text.secondary }}>{i18n.t('Session.actions.edit')}</Typography>
                    &nbsp;
                </Box>
            </SignEvalButton>
            <SwipeableDrawer
                anchor="bottom"
                open={openDrawer}
                onClose={handleClose}
                onOpen={handleClickOpen}
                disableSwipeToOpen={true}
                PaperProps={{
                    sx: {
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        backgroundColor: theme.palette.background.default,
                        backgroundImage: 'none',
                        borderRadius: '15px 15px 0 0',
                        paddingBottom: '1em',
                        width: '100%',
                        height: 'auto'
                    }
                }}
            >
                {/* Bar which indicates that you can slide the drawer */}
                <Box sx={{ width: 45, height: 6, backgroundColor: theme.palette.text.primary, borderRadius: '25px', marginTop: '1.25em', marginBottom: '1em' }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '92.5%', color: '#000', marginTop: '0.5em' }}>
                    <TitleContentInfo title={i18n.t('UpdateSession.title')} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', margin: '0.5em auto 0 auto', width: '100%', gap: '1em' }}>
                        <SignEvalTextField
                            icon={<ArticleIcon />}
                            iconPosition="end"
                            defaultValue={session.sessionName || ''}
                            registerProps={{
                                ...register('sessionName', {
                                    required: true,
                                    validate: (value) => isNameValid(value) || i18n.t('CreateSession.error.sessionName')
                                }),
                                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                                    clearErrors('sessionName');
                                    setValue('sessionName', e.target.value);
                                }
                            }}
                            id="sessionName"
                            label={i18n.t('CreateSession.inputs.sessionName') + ' *'}
                            error={!!errors.sessionName}
                            type="text"
                        />
                        <Controller
                            name="training"
                            defaultValue={session.trainingLabel || ''}
                            control={control}
                            rules={{ required: i18n.t('CreateSession.error.trainingName') }}
                            render={({ field }) => (
                                <Autocomplete
                                    {...field}
                                    options={trainings.map((training) => training.label)}
                                    freeSolo
                                    value={field.value}
                                    isOptionEqualToValue={(option, value) => option === value}
                                    noOptionsText={i18n.t('CreateSession.errors.noTrainings')}
                                    onChange={(_, newValue) => {
                                        if (newValue && !trainings.some((t) => t.label === newValue)) {
                                            setValue('training', newValue);
                                            clearErrors('training');
                                        } else {
                                            field.onChange(newValue || '');
                                            clearErrors('training');
                                        }
                                    }}
                                    onInputChange={(_, newInputValue) => {
                                        setValue('training', newInputValue);
                                        clearErrors('training');
                                    }}
                                    renderInput={(params) => (
                                        <SignEvalTextField
                                            {...params}
                                            label={i18n.t('CreateSession.inputs.trainingName') + ' *'}
                                            InputProps={{
                                                ...params.InputProps,
                                                sx: {
                                                    maxHeight: '3em',
                                                    borderRadius: '10px',
                                                    color: theme.palette.text.primary
                                                }
                                            }}
                                            InputLabelProps={{
                                                sx: { fontSize: '0.85em', color: theme.palette.text.primary }
                                            }}
                                            error={!!errors.training}
                                        />
                                    )}
                                    ListboxProps={{
                                        sx: {
                                            width: '100%',
                                            backgroundColor: theme.palette.background.paper,
                                            color: theme.palette.text.primary,
                                            borderRadius: '10px',
                                            marginTop: '0.5em',
                                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                                            maxHeight: '9em',
                                            overflow: 'auto',
                                            listStyle: 'none',
                                            position: 'absolute'
                                        }
                                    }}
                                />
                            )}
                        />
                        <Controller
                            name="typeTraining"
                            defaultValue={session.typeTraining || ''}
                            control={control}
                            render={({ field }) => (
                                <FormControl fullWidth>
                                    <InputLabel
                                        id="demo-simple-select-label"
                                        sx={{
                                            fontSize: '0.85em',
                                            color: theme.palette.text.primary,
                                            '&.Mui-focused': { color: theme.palette.text.custom }
                                        }}
                                    >
                                        {i18n.t('CreateSession.inputs.trainingType')}
                                    </InputLabel>
                                    <Select
                                        {...field}
                                        value={field.value || ''}
                                        labelId="demo-simple-select-label"
                                        label={i18n.t('CreateSession.inputs.trainingType')}
                                        onChange={(e) => field.onChange(e.target.value)}
                                        sx={{
                                            maxHeight: '3em',
                                            borderRadius: '10px',
                                            color: theme.palette.text.primary,
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: theme.palette.text.custom
                                            }
                                        }}
                                        MenuProps={{
                                            PaperProps: {
                                                sx: {
                                                    boxShadow: 'none',
                                                    backgroundColor: 'transparent'
                                                }
                                            },
                                            MenuListProps: {
                                                sx: {
                                                    width: '100%',
                                                    backgroundColor: theme.palette.background.paper,
                                                    color: theme.palette.text.primary,
                                                    borderRadius: '20px',
                                                    marginTop: '0.5em'
                                                }
                                            }
                                        }}
                                    >
                                        {typeTrainings.map((option) => (
                                            <MenuItem sx={{ margin: '0 auto', width: '95%', borderRadius: '10px' }} key={option} value={option}>
                                                {option}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        />
                        <Controller
                            name="societyId"
                            control={control}
                            defaultValue={session.societyId ?? ''}
                            render={({ field }) => (
                                <Autocomplete
                                    options={societies || []}
                                    getOptionLabel={(option: SocietyData) => option.name ?? ''}
                                    value={(societies || []).find((s) => String(s.id) === String(field.value)) || null}
                                    isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
                                    onChange={(_, newValue) => {
                                        field.onChange(newValue ? String(newValue.id) : '');
                                    }}
                                    renderInput={(params) => (
                                        <SignEvalTextField
                                            {...params}
                                            label={i18n.t('CreateSession.inputs.sessionSociety')}
                                            InputProps={{
                                                ...params.InputProps,
                                                sx: { maxHeight: '3em', borderRadius: '10px', color: theme.palette.text.primary }
                                            }}
                                            InputLabelProps={{ sx: { fontSize: '0.85em', color: theme.palette.text.primary } }}
                                        />
                                    )}
                                    ListboxProps={{
                                        sx: {
                                            width: '100%',
                                            backgroundColor: theme.palette.background.paper,
                                            color: theme.palette.text.primary,
                                            borderRadius: '10px',
                                            marginTop: '0.5em',
                                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                                            maxHeight: '9em',
                                            overflow: 'auto',
                                            listStyle: 'none',
                                            position: 'absolute'
                                        }
                                    }}
                                />
                            )}
                        />
                    </Box>
                    {slots.length > 0 && <SlotPicker slotList={slots} onSlotChange={onSlotChange} editMode={true} onCloseDrawer={handleClose} />}
                    {isLoading ? (
                        <CircularProgress sx={{ margin: '1.5em auto 1em auto', height: '3em', color: theme.palette.text.custom }} />
                    ) : (
                        <SignEvalButton sx={{ margin: '1.5em 0 1em 0' }} size="large" backgroundColor="blue" onClick={handleSubmit(onSubmit)}>
                            {i18n.t('CreateSession.button.update')}
                        </SignEvalButton>
                    )}
                </Box>
            </SwipeableDrawer>
        </>
    );
};

export default DrawerEditSession;
